import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";
import { catchError, distinctUntilChanged, map, share, shareReplay, skip, take, timeout } from "rxjs/operators";
import { UserAccount, UserService } from "src/core/backend/controllers";

export interface RoleCheckConfig {
  some?: string[];
  every?: string[];
}

@Injectable({ providedIn: "root" })
export class UserStateService {
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _userSubject = new BehaviorSubject<UserAccount | null>(null);

  readonly user$ = this._userSubject.pipe(
    skip(1),
    distinctUntilChanged((a, b) => (a && a.id) === (b && b.id)),
    shareReplay(1)
  );

  readonly isEditor$ = this.hasRoles$({ some: ["Editor", "Admin"] });
  readonly isAdmin$ = this.hasRoles$({ some: ["Admin"] });
  readonly isLoggedIn$ = this.user$.pipe(map((user) => user != null));

  get currentUser() {
    return this._userSubject.getValue();
  }

  constructor() {
    this.queryState();
    this.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private handleLoginError(error: HttpErrorResponse) {
    if (error.status !== HttpStatusCode.Unauthorized) {
      console!.warn("User state error:", error.message);
    }
    return of(null);
  }

  private queryState() {
    firstValueFrom(
      this.userService.getCurrentUser().pipe(
        //
        timeout(5000),
        catchError(this.handleLoginError)
      )
    ).then((user) => this._userSubject.next(user));
  }

  hasRoles$(config: RoleCheckConfig): Observable<boolean> {
    return this.user$.pipe(
      map((user) => {
        if (!user) return false;
        const hasSomeRole = config.some == null || config.some.some((role) => user.roles.includes(role));
        const hasEveryRole = config.every == null || config.every.every((role) => user.roles.includes(role));
        return hasSomeRole && hasEveryRole;
      }),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  redirectToLogin(returnUrl: string | null) {
    const _returnUrl = returnUrl == null || returnUrl.indexOf("/login") >= 0 ? "" : `?returnUrl=${returnUrl}`;

    window.location.href = `/account/login${_returnUrl}`;
  }

  gotoLogin() {
    this.redirectToLogin(window.location.href);
  }

  logout() {
    return of((window.location.href = `/account/logout`));
  }

  isLoggedIn() {
    return this.user$.pipe(
      take(1),
      map((user) => user != null),
      share()
    );
  }
}
